import "antd/dist/reset.css";
import "react-grid-layout/css/styles.css";
import "../src/components/util.css";
import "pages/[orgId]/dashboards/global-overrides.css";
import "../src/components/design-system.css";
import "../src/theme/tokens/__generated__/vzbl-light.css";
import "../src/theme/tokens/__generated__/vzbl-dark.css";
import type { AppProps } from "next/app";
import { useEffect, useState, PropsWithChildren } from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { SessionProvider, useSession } from "next-auth/react";
import { ConfigProvider, theme } from "antd";
import { AnimatePresence } from "framer-motion";
import { darkTheme, lightTheme } from "src/config/theme";
import { CssVars } from "src/components/CssVars";
import { useColorScheme } from "src/hooks/useColorScheme";
import { inter } from "src/fonts";
import { useOrg } from "src/hooks/useOrg";

const queryClient = new QueryClient();

export default function App(props: AppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider session={props.pageProps.session}>
        <StyledApp {...props} />
      </SessionProvider>
    </QueryClientProvider>
  );
}

function StyledApp({ Component, ...props }: AppProps) {
  const style = useColorScheme();
  const [AntCssProvider, setAntCssProvider] = useState(
    () =>
      function NoCssProvider({ children }: PropsWithChildren) {
        // Hack until I figure out SSR CSS
        return <>{children}</>;
      }
  );

  const session = useSession();
  const { id: orgId } = useOrg();
  useEffect(() => {
    if (window && session.status === "authenticated" && (window as any).pendo) {
      (window as any).pendo.initialize({
        visitor: {
          id: session.data.user.id,
          email: session.data.user.email,
          full_name: session.data.user.name,
          orgId,
        },
      });
    }
  }, [
    orgId,
    session.status,
    session.data?.user.id,
    session.data?.user.email,
    session.data?.user.name,
  ]);

  useEffect(() => {
    setAntCssProvider(
      () =>
        function CssThemeProvider({ children }: PropsWithChildren<{}>) {
          const css = style === "dark" ? darkTheme : lightTheme;
          const themeConfig = {
            token: { ...css.token, ...inter.style },
            algorithm:
              style === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
          };

          return (
            <ConfigProvider theme={themeConfig}>
              <>{children}</>
            </ConfigProvider>
          );
        }
    );

    // Set the theme class on the root element
    document.documentElement.dataset.theme = style;
  }, [style]);

  return (
    <AntCssProvider>
      <AnimatePresence
        onExitComplete={() => window.scrollTo(0, 0)}
        mode="wait"
        initial={false}
      >
        <CssVars className={inter.className}>
          <Component {...props.pageProps} />
        </CssVars>
      </AnimatePresence>
    </AntCssProvider>
  );
}
